import { useEffect, useState, useRef } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default function MapaTelecom() {
  const fincaId = useFincaId();
  const [equipos, setEquipos] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [sectorId, setSectorId] = useState('');
  
  const [mostrarAntenas, setMostrarAntenas] = useState(true);
  const [mostrarPostes, setMostrarPostes] = useState(true);
  
  const mapRef = useRef(null);
  const capaPiscinasRef = useRef(null);
  const capaEquiposRef = useRef(null);
  const mapContainerRef = useRef(null);

  // 1. Inicializar el mapa
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    
    const map = L.map(mapContainerRef.current).setView([-3.2581, -79.9553], 14);
    mapRef.current = map;

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Esri, Maxar',
      maxZoom: 22,
      maxNativeZoom: 19,
    }).addTo(map);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 22,
      opacity: 0.5,
    }).addTo(map);

    capaPiscinasRef.current = L.featureGroup().addTo(map);
    capaEquiposRef.current = L.featureGroup().addTo(map);

    return () => map.remove();
  }, []);

  // 2. Cargar datos
  useEffect(() => {
    if (!fincaId) return;
    
    api.get(`/sectores/finca/${fincaId}`)
      .then(({ data }) => setSectores(data || []))
      .catch(err => console.error("Error cargando sectores", err));

    api.get('/piscinas')
      .then(({ data }) => setPiscinas(data || []))
      .catch(err => console.error("Error cargando piscinas", err));
      
    api.get('/telecom')
      .then(({ data }) => setEquipos(data || []))
      .catch(err => console.error("Error cargando equipos", err));
  }, [fincaId]);

  // 3. DIBUJAR PISCINAS (Referencia visual)
  useEffect(() => {
    if (!mapRef.current || !capaPiscinasRef.current) return;
    
    capaPiscinasRef.current.clearLayers();

    const piscinasFiltradas = sectorId 
      ? piscinas.filter(p => p.sector_id === sectorId)
      : piscinas;

    piscinasFiltradas.forEach(p => {
      // ✅ Manejo de seguridad: a veces la geometría viene como string desde la BD
      let geo = p.geometria;
      if (typeof geo === 'string') {
        try { geo = JSON.parse(geo); } catch(e) { return; }
      }
      
      if (!geo || !geo.coordinates || !geo.coordinates[0]) return;
      
      const coords = geo.coordinates[0].map(([lng, lat]) => [lat, lng]);
      
      // ✅ Hacemos la piscina mucho más visible (verde)
      const poly = L.polygon(coords, {
        color: '#2E7D32', // Verde oscuro
        weight: 2,
        dashArray: '5, 5',
        fillColor: '#4CAF50', // Verde claro
        fillOpacity: 0.2, // Más visible
        interactive: false // No clickeable para evitar conflictos
      });

      // ✅ Mostrar el nombre permanentemente
      poly.bindTooltip(`<b>${p.codigo}</b>`, { 
        permanent: true, 
        direction: 'center',
        className: 'bg-white/80 text-xs font-bold border-0 shadow-none text-green-900'
      });
      
      capaPiscinasRef.current.addLayer(poly);
    });
  }, [piscinas, sectorId]);

  // 4. DIBUJAR ANTENAS Y POSTES
  useEffect(() => {
    if (!mapRef.current || !capaEquiposRef.current) return;

    capaEquiposRef.current.clearLayers();

    const equiposFiltrados = sectorId 
      ? equipos.filter(eq => eq.sector_id === sectorId)
      : equipos;

    const equiposVisibles = equiposFiltrados.filter(eq => {
      if (eq.tipo === 'antena' && !mostrarAntenas) return false;
      if (eq.tipo === 'poste' && !mostrarPostes) return false;
      return true;
    });

    equiposVisibles.forEach(eq => {
      const isAntena = eq.tipo === 'antena';
      const color = isAntena ? '#3B82F6' : '#EF4444'; 
      // ✅ CORREGIDO: 2400 metros = 2.4 km
      const radiusMeters = isAntena ? 2400 : 240; 
      
      L.circle([eq.lat, eq.lng], {
        radius: radiusMeters,
        color: color,
        fillColor: color,
        fillOpacity: 0.15,
        weight: 2,
        dashArray: '5, 5',
        interactive: false // El halo no debe bloquear clics
      }).addTo(capaEquiposRef.current);

      const iconSize = 40; 
      const icon = L.divIcon({
        html: `<div style="background:${color}; width:${iconSize}px; height:${iconSize}px; border-radius:50%; border:2px solid white; box-shadow:0 4px 8px rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; color:white; font-size:20px;">
                   ${isAntena ? '📡' : '📍'}
                 </div>`,
        className: 'custom-icon',
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2]
      });

      L.marker([eq.lat, eq.lng], { icon })
        .addTo(capaEquiposRef.current)
        .bindPopup(`
          <div class="text-sm font-sans">
            <h3 class="font-bold text-base mb-1 border-b pb-1">${eq.nombre}</h3>
            <p><b>Tipo:</b> ${eq.tipo.toUpperCase()}</p>
            <p><b>Radio de cobertura:</b> ${isAntena ? '2.4 km' : '240 m'}</p>
            <p><b>Sector:</b> ${eq.sector_nombre || 'N/A'}</p>
            <p><b>Obs:</b> ${eq.observaciones || 'Ninguna'}</p>
          </div>
        `);
    });
  }, [equipos, sectorId, mostrarAntenas, mostrarPostes]);

  // ✅ 5. AJUSTAR EL ZOOM PARA VER TODO (Piscinas + Equipos)
  useEffect(() => {
    if (!mapRef.current) return;
    
    // Unir las capas de piscinas y equipos para calcular los límites
    const group = L.featureGroup([capaPiscinasRef.current, capaEquiposRef.current]);
    const bounds = group.getBounds();
    
    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
    }
  }, [piscinas, equipos, sectorId]); // Se recalcula cuando cambian los filtros

  const FiltroChip = ({ activo, onClick, icono, label, colorActivo }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition font-medium text-sm shadow-sm ${
        activo
          ? `${colorActivo} text-white border-transparent`
          : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
      }`}
    >
      <span className="text-base">{icono}</span>
      <span>{label}</span>
      {activo ? <span className="text-[10px] ml-1">✓</span> : <span className="text-[10px] ml-1 opacity-50">○</span>}
    </button>
  );

  return (
    <div className="p-6 h-screen flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-primary">Mapa de Telecomunicaciones</h1>
        
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-gray-700">Filtrar por Sector:</label>
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[200px] bg-white"
          >
            <option value="">Todos los sectores</option>
            {sectores.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-3 mb-4 items-center flex-wrap">
        <FiltroChip 
          activo={mostrarAntenas} 
          onClick={() => setMostrarAntenas(!mostrarAntenas)} 
          icono="📡" 
          label="Antenas (Cobertura 2.4km)" 
          colorActivo="bg-blue-600"
        />
        <FiltroChip 
          activo={mostrarPostes} 
          onClick={() => setMostrarPostes(!mostrarPostes)} 
          icono="📍" 
          label="Postes (Cobertura 240m)" 
          colorActivo="bg-red-500"
        />
        
        <div className="text-sm text-gray-500 ml-auto self-center bg-gray-100 px-3 py-1 rounded-lg">
          Mostrando {sectorId ? equipos.filter(e => e.sector_id === sectorId).length : equipos.length} equipos
        </div>
      </div>

      <div ref={mapContainerRef} className="flex-1 rounded-xl shadow border-2 border-gray-200 z-0" />
    </div>
  );
}